<template>
  <div class="batch-page">
    <el-card class="box-card">
      <div slot="header" class="clearfix header-bar">
        <span class="page-title">年度盘点批次</span>
        <el-button type="primary" icon="el-icon-plus" size="small" @click="openCreate">新建批次</el-button>
      </div>

      <el-alert
        type="info"
        :closable="false"
        title="批次按年度建立，盘点计划自动归属对应年份批次。年终核对会一次性结算待核实、统计全院盘亏，并关闭批次（不可重复）。"
        style="margin-bottom: 14px"
      />

      <el-table :data="batches" v-loading="loading" border size="small">
        <el-table-column prop="year" label="年度" width="100" align="center" />
        <el-table-column prop="name" label="批次名称" min-width="200" show-overflow-tooltip />
        <el-table-column label="状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 'closed' ? 'info' : 'success'" size="small">
              {{ scope.row.status === 'closed' ? '已核对' : '进行中' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="核对时间" width="160" align="center">
          <template slot-scope="scope">{{ fmtTime(scope.row.closed_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="220" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" class="op-btn" @click="openDiff(scope.row)">查看差异</el-button>
            <el-button
              v-if="scope.row.status !== 'closed'"
              type="text" class="op-btn op-warn"
              @click="reconcile(scope.row)"
            >年终核对</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 新建批次 -->
    <el-dialog title="新建年度批次" :visible.sync="createVisible" width="420px" :close-on-click-modal="false">
      <el-form :model="createForm" label-width="80px" size="small">
        <el-form-item label="年度" required>
          <el-input v-model="createForm.year" placeholder="如 2026（4 位数字）" maxlength="4" />
        </el-form-item>
        <el-form-item label="批次名称">
          <el-input v-model="createForm.name" placeholder="选填，默认「2026年度全院盘点」" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="createVisible = false">取 消</el-button>
        <el-button type="primary" size="small" :loading="creating" @click="submitCreate">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 差异总表 -->
    <el-dialog :title="diffTitle" :visible.sync="diffVisible" width="860px" top="5vh" :close-on-click-modal="false">
      <div v-loading="diffLoading">
        <el-radio-group v-model="diffKind" size="mini" @change="loadDiff">
          <el-radio-button label="">全部</el-radio-button>
          <el-radio-button label="cross_dept">跨科室</el-radio-button>
          <el-radio-button label="pending">待核实</el-radio-button>
          <el-radio-button label="overage">盘盈</el-radio-button>
          <el-radio-button label="badcode">坏码</el-radio-button>
          <el-radio-button label="shortage">盘亏</el-radio-button>
        </el-radio-group>
        <el-table :data="diffs" border size="mini" max-height="440" style="margin-top: 10px">
          <el-table-column prop="equipment_id" label="设备ID / 扫码内容" min-width="200" show-overflow-tooltip />
          <el-table-column label="类型" width="100" align="center">
            <template slot-scope="scope">{{ kindText(scope.row.kind) }}</template>
          </el-table-column>
          <el-table-column prop="true_department" label="真实科室" width="110" show-overflow-tooltip />
          <el-table-column prop="found_department" label="发现科室" width="110" show-overflow-tooltip />
          <el-table-column label="已处理" width="80" align="center">
            <template slot-scope="scope">
              <el-tag :type="scope.row.resolved ? 'success' : 'info'" size="mini">
                {{ scope.row.resolved ? '是' : '否' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="note" label="备注" min-width="200" show-overflow-tooltip />
        </el-table>
        <div v-if="!diffLoading && diffs.length === 0" class="section-empty" style="margin-top: 10px">
          该筛选条件下暂无差异记录。
        </div>
      </div>
      <div slot="footer">
        <el-button size="small" @click="diffVisible = false">关 闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import api from '../api/inventory'

export default {
  name: 'InventoryBatch',
  data() {
    return {
      loading: false,
      batches: [],
      // 新建
      createVisible: false,
      creating: false,
      createForm: { year: '', name: '' },
      // 差异
      diffVisible: false,
      diffLoading: false,
      diffTitle: '',
      diffKind: '',
      diffs: [],
      currentBatchId: null,
    }
  },
  created() {
    this.fetchBatches()
  },
  methods: {
    fetchBatches() {
      this.loading = true
      api.listBatches()
        .then(res => { this.batches = res.data || [] })
        .catch(() => {})
        .finally(() => { this.loading = false })
    },
    openCreate() {
      this.createForm = { year: '', name: '' }
      this.createVisible = true
    },
    submitCreate() {
      const year = (this.createForm.year || '').trim()
      if (!/^\d{4}$/.test(year)) {
        this.$message.warning('请输入 4 位年度数字（如 2026）')
        return
      }
      this.creating = true
      api.createBatch({ year: parseInt(year, 10), name: (this.createForm.name || '').trim() })
        .then(() => {
          this.$message.success('批次已创建')
          this.createVisible = false
          this.fetchBatches()
        })
        .catch(err => {
          const detail = err.response && err.response.data && err.response.data.detail
          if (detail) this.$message.error(detail)
        })
        .finally(() => { this.creating = false })
    },
    openDiff(row) {
      this.currentBatchId = row.id
      this.diffTitle = `差异总表 — ${row.year} 年度`
      this.diffKind = ''
      this.diffs = []
      this.diffVisible = true
      this.loadDiff()
    },
    loadDiff() {
      if (!this.currentBatchId) return
      this.diffLoading = true
      const params = this.diffKind ? { kind: this.diffKind } : {}
      api.getBatchDifferences(this.currentBatchId, params)
        .then(res => { this.diffs = res.data || [] })
        .catch(() => {})
        .finally(() => { this.diffLoading = false })
    },
    reconcile(row) {
      this.$confirm(
        `确定对 ${row.year} 年度批次执行年终核对吗？将结算待核实并统计全院盘亏，且不可重复执行。`,
        '年终核对',
        { type: 'warning' }
      ).then(() => api.reconcileBatch(row.id))
        .then(res => {
          const r = res.data && res.data.result
          if (r) {
            this.$message.success(
              `核对完成：涉及 ${r.plan_count} 个计划，盘亏 ${r.shortage_count} 台`
            )
          } else {
            this.$message.success('核对完成')
          }
          this.fetchBatches()
        })
        .catch(() => {})
    },
    kindText(k) {
      return { cross_dept: '跨科室', pending: '待核实', overage: '盘盈', badcode: '坏码', shortage: '盘亏' }[k] || k
    },
    fmtTime(v) {
      if (!v) return '-'
      return String(v).replace('T', ' ').slice(0, 16)
    },
  },
}
</script>

<style scoped>
.batch-page {
  padding: 16px;
}
.box-card {
  /* 2026-10-21：铺满整窗 + 面板底色改豆沙绿（与全站护眼主题统一），不再限宽留白 */
  width: 100%;
  background-color: #f6f1e7;
  border: 1px solid #e3dcc8;
}
.header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.page-title {
  font-size: 18px;
  font-weight: bold;
  color: #303133;
}
.op-btn {
  padding: 0;
  font-weight: bold;
}
.op-warn {
  color: #e6a23c;
}
.section-empty {
  font-size: 13px;
  color: #909399;
}
</style>
